import { Control, Controller } from "react-hook-form";
import { ShieldCheck } from "lucide-react";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledRestrictionRow } from "@/components/ui/customs/controlled-restriction-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { currenciesOptions } from "@/constants/options/currencies.option";
import { CustomPolicyFormData } from "./custom-policy.form";
import { CustomPolicySectionCard } from "./customPolicySectionCard";
import { useTranslation } from "react-i18next";

type Props = {
  control: Control<CustomPolicyFormData>;
  isSuperAdmin: boolean;
  maxPurchase: boolean;
};

export const CustomPolicyRestrictionsSection = ({ control, isSuperAdmin, maxPurchase }: Props) => {
  const { t } = useTranslation("boCustoms");
  return (
    <CustomPolicySectionCard icon={ShieldCheck} title={t("policyRestrictions.title")}>
      <div className="flex flex-col">
        <ControlledRestrictionRow
          control={control}
          booleanName="maxBottles"
          valueName="maxBottlesValue"
          label={t("policyRestrictions.maxBottlesLabel")}
          placeholder={t("policyRestrictions.maxBottlesPlaceholder")}
          disabled={!isSuperAdmin}
        />

        <Separator className="my-4" />

        <ControlledRestrictionRow
          control={control}
          booleanName="maxLiters"
          valueName="maxLitersValue"
          label={t("policyRestrictions.maxLitersLabel")}
          placeholder={t("policyRestrictions.maxLitersPlaceholder")}
          inputType="float"
          disabled={!isSuperAdmin}
        />

        <Separator className="my-4" />

        <ControlledRestrictionRow
          control={control}
          booleanName="maxPurchase"
          valueName="maxPurchaseValue"
          label={t("policyRestrictions.maxPurchaseLabel")}
          placeholder={t("policyRestrictions.maxPurchasePlaceholder")}
          disabled={!isSuperAdmin}
          rightElement={
            <Controller
              control={control}
              name="maxPurchaseCurrency"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!isSuperAdmin || !maxPurchase}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currenciesOptions.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          }
        />

        <Separator className="my-4" />

        <ControlledRestrictionRow
          control={control}
          booleanName="maxAlcoholPercentage"
          valueName="maxAlcoholPercentageValue"
          label={t("policyRestrictions.maxAlcoholPercentageLabel")}
          placeholder={t("policyRestrictions.maxAlcoholPercentagePlaceholder")}
          inputType="float"
          disabled={!isSuperAdmin}
        />

        <Separator className="my-4" />

        <div className="flex flex-col gap-3">
          <ControlledCheckbox
            control={control}
            name="divideShipmentOverDays"
            label={t("policyRestrictions.divideShipmentOverDaysLabel")}
            disabled={!isSuperAdmin}
          />
          <ControlledCheckbox
            control={control}
            name="dapOnly"
            label={t("policyRestrictions.dapOnlyLabel")}
            disabled={!isSuperAdmin}
          />
          <ControlledCheckbox
            control={control}
            name="noShipmentToPrivateIndividual"
            label={t("policyRestrictions.noShipmentToPrivateIndividualLabel")}
            disabled={!isSuperAdmin}
          />
          <ControlledCheckbox
            control={control}
            name="sampleOnly"
            label={t("policyRestrictions.sampleOnlyLabel")}
            disabled={!isSuperAdmin}
          />
        </div>

        <Separator className="my-4" />

        <Controller
          control={control}
          name="freightAvailable"
          render={({ field }) => (
            <div className="flex items-center gap-4">
              <span className="w-44 shrink-0 text-sm font-medium text-gray-600">
                {t("policyRestrictions.freightLabel")}
              </span>
              <RadioGroup
                value={field.value ? "available" : "unavailable"}
                onValueChange={(v) => field.onChange(v === "available")}
                disabled={!isSuperAdmin}
                className="flex items-center gap-3"
              >
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="available" id="freight-available" />
                  <Label htmlFor="freight-available" className="cursor-pointer text-sm font-medium">
                    {t("policyRestrictions.freightAvailable")}
                  </Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="unavailable" id="freight-unavailable" />
                  <Label
                    htmlFor="freight-unavailable"
                    className="cursor-pointer text-sm font-medium"
                  >
                    {t("policyRestrictions.freightUnavailable")}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
        />
      </div>
    </CustomPolicySectionCard>
  );
};
